import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, deleteDoc, updateDoc, query, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Button, Card, Alert, Modal, Form } from 'react-bootstrap';
import './ManageFriends.css'; // Import the new CSS file

/**
 * ManageFriends component for managing user's friends
 * Allows users to view, edit, and delete friends
 */
const ManageFriends = () => {
  const [friends, setFriends] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [patterns, setPatterns] = useState({});
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFriend, setEditFriend] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedPattern, setSelectedPattern] = useState('');
  const [selectedShift, setSelectedShift] = useState('');

  // Fetch friends and companies on component mount
  useEffect(() => {
    const fetchFriendsAndCompanies = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('User not authenticated');
        }

        // Fetch friends
        const friendsRef = collection(db, 'users', currentUser.uid, 'friends');
        const friendsSnapshot = await getDocs(friendsRef);
        const friendsData = [];
        
        friendsSnapshot.forEach((doc) => {
          if (!doc.data().placeholder) { // Skip placeholder document
            friendsData.push({
              id: doc.id,
              ...doc.data()
            });
          }
        });
        
        setFriends(friendsData);

        // Fetch companies and patterns
        const shiftsRef = // Fetch from shiftPatterns collection (migrated from CSV)
collection(db, 'shiftPatterns');
        const shiftsSnapshot = await getDocs(shiftsRef);
        
        const uniqueCompanies = [];
        const companyPatterns = {};
        
        shiftsSnapshot.forEach((doc) => {
          const data = doc.data();
          const companyName = data.company;
          const patternName = data.patternName;
          
          if (!uniqueCompanies.includes(companyName)) {
            uniqueCompanies.push(companyName);
          }
          
          if (!companyPatterns[companyName]) {
            companyPatterns[companyName] = [];
          }
          
          if (!companyPatterns[companyName].includes(patternName)) {
            companyPatterns[companyName].push(patternName);
          }
        });
        
        setCompanies(uniqueCompanies);
        setPatterns(companyPatterns);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFriendsAndCompanies();
  }, []);

  // Handle friend deletion
  const handleDelete = async (friendId) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      
      await deleteDoc(doc(db, 'users', currentUser.uid, 'friends', friendId));
      
      // Update local state
      setFriends(friends.filter(friend => friend.id !== friendId));
    } catch (error) {
      console.error('Error deleting friend:', error);
      setError('Failed to delete friend. Please try again.');
    }
  };

  // Open edit modal for a friend
  const handleEdit = (friend) => {
    setEditFriend(friend);
    setSelectedCompany(friend.company);
    setSelectedPattern(friend.pattern);
    setSelectedShift(friend.shift);
    
    // Load shifts for the selected company and pattern
    fetchShifts(friend.company, friend.pattern);
    
    setShowEditModal(true);
  };

  // Handle company change in edit modal
  const handleCompanyChange = (e) => {
    const company = e.target.value;
    setSelectedCompany(company);
    
    // Reset pattern and shift when company changes
    if (patterns[company] && patterns[company].length > 0) {
      setSelectedPattern(patterns[company][0]);
    } else {
      setSelectedPattern('');
    }
    setSelectedShift('');
    
    // Fetch shifts for the selected company and pattern
    fetchShifts(company, patterns[company][0]);
  };

  // Handle pattern change in edit modal
  const handlePatternChange = (e) => {
    const pattern = e.target.value;
    setSelectedPattern(pattern);
    setSelectedShift('');
    
    // Fetch shifts for the selected company and pattern
    fetchShifts(selectedCompany, pattern);
  };

  // Fetch shifts for a company and pattern
  const fetchShifts = async (company, pattern) => {
    if (company && pattern) {
      try {
        const shiftsRef = // Fetch from shiftPatterns collection (migrated from CSV)
collection(db, 'shiftPatterns');
        const q = query(
          shiftsRef, 
          where('company', '==', company),
          where('patternName', '==', pattern)
        );
        
        const querySnapshot = await getDocs(q);
        const shiftsData = [];
        
        querySnapshot.forEach((doc) => {
          shiftsData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setShifts(shiftsData);
        
        // Set default shift if available
        if (shiftsData.length > 0) {
          setSelectedShift(shiftsData[0].shift);
        }
      } catch (error) {
        console.error("Error fetching shifts:", error);
        setError("Failed to load shifts. Please try again.");
      }
    }
  };

  // Save edited friend
  const handleSave = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !editFriend) {
        throw new Error('User not authenticated or no friend selected');
      }
      
      // Find the shift ID
      const shiftsRef = // Fetch from shiftPatterns collection (migrated from CSV)
collection(db, 'shiftPatterns');
      const q = query(
        shiftsRef, 
        where('company', '==', selectedCompany),
        where('patternName', '==', selectedPattern),
        where('shift', '==', selectedShift)
      );
      
      const querySnapshot = await getDocs(q);
      let shiftId = null;
      
      querySnapshot.forEach((doc) => {
        shiftId = doc.id;
      });
      
      if (!shiftId) {
        throw new Error('Shift not found');
      }
      
      // Update friend document
      await updateDoc(doc(db, 'users', currentUser.uid, 'friends', editFriend.id), {
        company: selectedCompany,
        pattern: selectedPattern,
        shift: selectedShift,
        shiftId: shiftId
      });
      
      // Update local state
      setFriends(friends.map(friend => {
        if (friend.id === editFriend.id) {
          return {
            ...friend,
            company: selectedCompany,
            pattern: selectedPattern,
            shift: selectedShift,
            shiftId: shiftId
          };
        }
        return friend;
      }));
      
      // Close modal
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating friend:', error);
      setError('Failed to update friend. Please try again.');
    }
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <div>
      <Card>
        <Card.Header>
          <h2 className="mb-0">Manage Friends</h2>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          {friends.length === 0 ? (
            <Alert variant="info">You haven't added any friends yet.</Alert>
          ) : (
            <div className="friend-cards-container">
              {friends.map((friend) => (
                <div key={friend.id} className="friend-card mb-2 p-2 border rounded">
                  <h5 className="mb-2 friend-name">{friend.nick}</h5>
                  <div className="row g-0">
                    <div className="col-8 friend-info-col">
                      <div className="company-row">
                        <div className="detail-value">{friend.company}</div>
                      </div>
                      <div className="pattern-shift-row">
                        <span className="detail-value">{friend.pattern}</span>
                        <span className="detail-separator">•</span>
                        <span className="detail-value">{friend.shift}</span>
                      </div>
                    </div>
                    <div className="col-4 friend-actions-col">
                      <div className="friend-actions">
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          onClick={() => handleEdit(friend)}
                          className="edit-btn"
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDelete(friend.id)}
                          className="delete-btn"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Friend</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editFriend && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Nickname</Form.Label>
                <Form.Control
                  type="text"
                  value={editFriend.nick}
                  disabled
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Company</Form.Label>
                <Form.Select 
                  value={selectedCompany} 
                  onChange={handleCompanyChange}
                >
                  {companies.map((company, index) => (
                    <option key={index} value={company}>{company}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Pattern</Form.Label>
                <Form.Select 
                  value={selectedPattern} 
                  onChange={handlePatternChange}
                  disabled={!selectedCompany || !patterns[selectedCompany]}
                >
                  {selectedCompany && patterns[selectedCompany] && 
                    patterns[selectedCompany].map((pattern, index) => (
                      <option key={index} value={pattern}>{pattern}</option>
                    ))
                  }
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Shift</Form.Label>
                <Form.Select 
                  value={selectedShift} 
                  onChange={(e) => setSelectedShift(e.target.value)}
                  disabled={!shifts.length}
                >
                  {shifts.map((shift, index) => (
                    <option key={index} value={shift.shift}>{shift.shift}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageFriends;
